import * as React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import style from './style/WDnd.less';

export interface WDndProps extends React.ClassAttributes<any> {
    title: any;
    lists: any;
    group?: Array<any>;
    groupStyle?: Array<any>;
    plusStyle?: any;
    listStyle?: React.CSSProperties;
    disabled?: boolean;
    enableFilter?: boolean;
    flexFormat?: boolean;
    onChange: (lists: any) => void;
}

export interface WDndState extends React.ClassAttributes<any> {
    plusStyle: any;
    filterVals: any;
}

const reorder = (list: any, startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const move = (source: any, destination: any, droppableSource: any, droppableDestination: any) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);
    destClone.splice(droppableDestination.index, 0, removed);
    const result: any = {};
    result[droppableSource.droppableId] = sourceClone;
    result[droppableDestination.droppableId] = destClone;
    return result;
};

const getItemStyle = (isDragging: boolean, draggableStyle: any, flexFormat: boolean, plusStyle?: any) => ({
    background: isDragging ? 'white' : '#EEE',
    boxSizing: 'border-box',
    width: plusStyle && plusStyle.width && !flexFormat ? plusStyle.width : null,
    margin: plusStyle && plusStyle.margin ? plusStyle.margin : null,
    // styles we need to apply on draggables
    ...draggableStyle
});

const getListStyle = (isDraggingOver: boolean, plusStyle?: any) => ({
    background: isDraggingOver ? '#CCC' : 'white',
    width: plusStyle && plusStyle.width ? plusStyle.width : null,
    height: plusStyle && plusStyle.height ? plusStyle.height : null,
});

const getFlexStyle = (flexFormat: boolean): React.CSSProperties => {
    if (!flexFormat) {
        return {};
    }
    return { display: 'flex', flexDirection: 'row', flexWrap: 'wrap' };
};

export class WDnd extends React.Component<WDndProps, WDndState> {

    constructor(props: any) {
        super(props);

        this.onDragEnd = this.onDragEnd.bind(this);
        this.renderDndList = this.renderDndList.bind(this);
        this.onFilter = this.onFilter.bind(this);

        this.state = {
            plusStyle: this.props.plusStyle ? this.props.plusStyle : null,
            filterVals: {}
        };
    }

    onDragEnd(data: any) {
        // console.log(data);
        const { source, destination } = data;

        // dropped outside the list
        if (!destination) {
            return;
        }

        const lists = { ... this.props.lists };
        const sourceId = source.droppableId;
        const destId = destination.droppableId;

        if (sourceId === destId) {
            lists[sourceId] = reorder(
                lists[sourceId],
                source.index,
                destination.index
            );
        } else {
            const result = move(
                lists[sourceId],
                lists[destId],
                source,
                destination
            );
            lists[sourceId] = result[sourceId];
            lists[destId] = result[destId];
        }

        this.props.onChange(lists);
    }

    render() {
        return (
            <div>
                {this.renderDnd()}
            </div>
        );
    }

    renderDnd() {
        const droppable: Array<JSX.Element> = [];

        if (this.props.group) {
            this.props.group.forEach((g: Array<any>, i: number) => {
                const droppableGroup: Array<JSX.Element> = [];
                g.forEach((key) => {
                    const ele = this.renderDndList(key);
                    if (ele) {
                        droppableGroup.push(ele);
                    }
                });
                droppable.push(
                    <div
                        key={'k_drop_' + i}
                        className={style.droppableGroup}
                        style={this.props.groupStyle && this.props.groupStyle[i] ? this.props.groupStyle[i] : {}}
                    >
                        {droppableGroup}
                    </div>
                );
            });
        } else if (this.props.lists) {
            for (const key of Object.keys(this.props.lists)) {
                const ele = this.renderDndList(key);
                if (ele) {
                    droppable.push(ele);
                }
            }
        }

        return (
            <div>
                <DragDropContext onDragEnd={this.onDragEnd}>
                    {droppable}
                </DragDropContext>
            </div>
        );
    }

    renderDndList(key: string) {
        if (this.props.lists && this.props.lists.hasOwnProperty(key)) {
            const list = this.props.lists[key];
            return (
                <div className={style.droppableColumn} key={`column-${key}`}>
                    <div
                        className={style.droppableTitle}
                        style={this.state.plusStyle ? { width: this.state.plusStyle.width } : null}>
                        {this.props.title[key] ? this.props.title[key] : key}
                    </div>
                    {this.props.enableFilter &&
                        <input
                            type='text'
                            placeholder='Suche ...'
                            className={style.filter}
                            value={this.state.filterVals[key]}
                            onChange={(evt: any) => this.onFilter(key, evt)}
                        />
                    }
                    <Droppable droppableId={key}>
                        {(provided, snapshot) => (
                            <div
                                className={style.droppableList}
                                ref={provided.innerRef}
                                style={{
                                    ...getListStyle(snapshot.isDraggingOver, this.state.plusStyle), ...getFlexStyle(this.props.flexFormat),
                                    ...(this.props.listStyle || {})
                                }}>
                                {list.map((item: any, index: number) => (
                                    <Draggable
                                        key={item.id}
                                        draggableId={item.id + ''} // draggableId ist ein String
                                        isDragDisabled={this.props.disabled ? this.props.disabled : false}
                                        index={index}>
                                        {(provided, snapshot) => (
                                            <div
                                                className={style.droppableListItem}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={getItemStyle(
                                                    snapshot.isDragging,
                                                    provided.draggableProps.style,
                                                    this.props.flexFormat,
                                                    this.state.plusStyle
                                                )}>
                                                {item.content}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </div>
            );
        }
    }

    onFilter(key: string, evt: any) {
        const val = evt.target.value;
        const filterVals = this.state.filterVals;
        filterVals[key] = val;
        this.setState({ filterVals });
        console.log(filterVals);
    }
}
